import React from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import ThemeSwitcher from "@/components/common/ThemeSwitcher";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { WelcomeDialog } from "@/components/common/WelcomeDialog";
import { useTheme } from "@/lib/theme";
import { LayoutDashboard, Users, FileText, FolderSearch, GitPullRequest, BarChart3, Settings, Building2, LogOut, Menu, ShieldCheck, Palette, Database, FileWarning, GitBranchPlus, Activity, ChevronDown, Crown, Mail, ClipboardList, History, Sparkles, UserPlus, Package, Upload } from "lucide-react";

const NAV_GROUPS_DEFAULT = [
  {
    label: "Operations", i18nKey: "nav.operations",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", i18nKey: "nav.dashboard", testid: "sidebar-nav-dashboard" },
      { to: "/leads", icon: UserPlus, label: "Leads", testid: "sidebar-nav-leads" },
      { to: "/customers", icon: Users, label: "Customers", i18nKey: "nav.customers", testid: "sidebar-nav-customers" },
      { to: "/applications", icon: FileText, label: "Loan Applications", i18nKey: "nav.applications", testid: "sidebar-nav-applications" },
      { to: "/drafts", icon: History, label: "My Drafts", i18nKey: "nav.drafts", testid: "sidebar-nav-drafts" },
      { to: "/fo-queue", icon: ClipboardList, label: "FO Queue", i18nKey: "nav.foQueue", testid: "sidebar-nav-fo-queue" },
      { to: "/documents", icon: FolderSearch, label: "Documents (DMS)", i18nKey: "nav.documents", testid: "sidebar-nav-documents" },
    ],
  },
  {
    label: "Governance", i18nKey: "nav.governance",
    items: [
      { to: "/approvals", icon: GitPullRequest, label: "Approvals", i18nKey: "nav.approvals", testid: "sidebar-nav-approvals" },
      { to: "/maker-checker", icon: GitBranchPlus, label: "Maker-Checker Queue", testid: "sidebar-nav-mc" },
    ],
  },
  {
    label: "Insights", i18nKey: "nav.insights",
    items: [
      { to: "/reports", icon: BarChart3, label: "Reports", testid: "sidebar-nav-reports" },
      { to: "/reports/builder", icon: Sparkles, label: "Report Builder", i18nKey: "nav.reportBuilder", testid: "sidebar-nav-report-builder" },
      { to: "/mis", icon: Activity, label: "MIS Analytics", testid: "sidebar-nav-mis" },
    ],
  },
  {
    label: "Administration", i18nKey: "nav.administration",
    items: [
      { to: "/admin", icon: Settings, label: "Users", i18nKey: "nav.users", testid: "sidebar-nav-admin" },
      { to: "/admin/master", icon: Database, label: "Banks & Branches", i18nKey: "nav.masterData", testid: "sidebar-nav-master" },
      { to: "/admin/products", icon: Package, label: "Loan Products", testid: "sidebar-nav-products" },
      { to: "/admin/policy", icon: FileWarning, label: "Policy Rules", i18nKey: "nav.rules", testid: "sidebar-nav-policy" },
      { to: "/customers/legacy-upload", icon: Upload, label: "Legacy Upload", testid: "sidebar-nav-legacy-upload" },
      { to: "/notifications", icon: Mail, label: "Notifications", i18nKey: "nav.notifications", testid: "sidebar-nav-notifications" },
      { to: "/settings", icon: Palette, label: "Appearance", i18nKey: "nav.appearance", testid: "sidebar-nav-settings" },
    ],
  },
];

const SUPER_ADMIN_NAV = [
  {
    label: "Super Admin", i18nKey: "nav.superAdmin",
    items: [
      { to: "/super-admin", icon: Crown, label: "Tenant Overview", i18nKey: "nav.tenantOverview", testid: "sidebar-nav-superadmin" },
      { to: "/super-admin/banks/new", icon: Building2, label: "Onboard Bank", i18nKey: "nav.onboardBank", testid: "sidebar-nav-newbank" },
      { to: "/admin", icon: Users, label: "All Users", i18nKey: "nav.allUsers", testid: "sidebar-nav-allusers" },
      { to: "/settings", icon: Palette, label: "Appearance", i18nKey: "nav.appearance", testid: "sidebar-nav-settings" },
    ],
  },
];

function getNavGroups(role) {
  if (role === "SUPER_ADMIN") return SUPER_ADMIN_NAV;
  return NAV_GROUPS_DEFAULT;
}

function SidebarBody({ user, bank, onNavClick }) {
  const { t } = useTranslation();
  const groups = getNavGroups(user?.role);
  return (
    <div className="h-full flex flex-col bg-card border-r border-border">
      <div className="px-4 py-4 border-b border-border flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
          {user?.role === "SUPER_ADMIN" ? <Crown className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <div className="font-semibold heading text-sm truncate">{user?.role === "SUPER_ADMIN" ? "Traceroot Cloud" : (bank?.short_name || "Traceroot")}</div>
          <div className="text-[11px] text-muted-foreground -mt-0.5 truncate">{user?.role === "SUPER_ADMIN" ? "Super Admin Console" : (bank?.name || "Credit Automation")}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-thin">
        {groups.map((grp, gi) => (
          <div key={gi} className="mb-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold px-3 py-1.5">{grp.i18nKey ? t(grp.i18nKey) : grp.label}</div>
            <div className="space-y-0.5">
              {grp.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  onClick={onNavClick}
                  end={it.to === "/admin" || it.to === "/super-admin"}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-accent/12 text-accent font-medium border border-accent/25" : "text-foreground/80 hover:bg-muted/60 border border-transparent"}`
                  }
                >
                  <it.icon className="h-4 w-4" />
                  <span>{it.i18nKey ? t(it.i18nKey) : it.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 text-success" />
          <span>AES-GCM encrypted at rest</span>
        </div>
        <div className="mt-1.5 text-[11px] text-muted-foreground mono">{user?.role === "RO" && user?.region ? `Region: ${user.region}` : user?.branch ? `Branch: ${user.branch}` : (bank?.name ? `Bank: ${bank.short_name || bank.name}` : null)}</div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, bank, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { theme, themes } = useTheme();

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        <aside className="hidden lg:flex w-[260px] flex-shrink-0 sticky top-0 h-screen">
          <SidebarBody user={user} bank={bank} />
        </aside>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-[280px]">
            <SidebarBody user={user} bank={bank} onNavClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        <main className="flex-1 min-w-0 flex flex-col min-h-screen">
          <header className="sticky top-0 z-30 h-14 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85">
            <div className="h-full px-4 lg:px-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="topbar-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground -mb-0.5 truncate">{user?.role === "SUPER_ADMIN" ? "Super Admin · Multi-tenant" : `${bank?.short_name || "Traceroot"} · ${user?.role === "RO" && user?.region ? user.region + " Region" : user?.branch || "—"}`}</div>
                  <div className="text-sm font-semibold heading truncate">{pageTitle(location.pathname)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user?.role === "SUPER_ADMIN" && <Badge variant="outline" className="hidden md:inline-flex border-warning/40 text-warning bg-warning/10"><Crown className="h-3 w-3 mr-1" />SUPER ADMIN</Badge>}
                <Badge variant="secondary" className="hidden sm:inline-flex" data-testid="role-badge">{user?.role || "—"}</Badge>
                <div className="hidden md:block"><LanguageSwitcher variant="compact" /></div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1.5" data-testid="topbar-theme-button">
                      <Palette className="h-4 w-4" />
                      <span className="hidden sm:inline text-xs">{themes[theme]?.label?.split(" · ")[0] || "Theme"}</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-[420px] p-4">
                    <ThemeSwitcher inline />
                  </PopoverContent>
                </Popover>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="text-xs bg-accent text-accent-foreground">{initials}</AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:inline text-sm font-medium">{user?.name?.split(" - ")[0] || "User"}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="text-sm font-medium">{user?.name}</div>
                      <div className="text-xs text-muted-foreground">{user?.email}</div>
                      {bank && <div className="text-[11px] text-muted-foreground mt-1 mono">{bank.name}</div>}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/settings")}>Settings</DropdownMenuItem>
                    {user?.role === "SUPER_ADMIN" && <DropdownMenuItem onClick={() => navigate("/super-admin")}>Super Admin</DropdownMenuItem>}
                    {user?.role !== "SUPER_ADMIN" && <DropdownMenuItem onClick={() => navigate("/admin")}>Admin</DropdownMenuItem>}
                    <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-button">
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>
          <div className="px-4 lg:px-6 py-5 max-w-[1600px] mx-auto flex-1 w-full">
            <Outlet />
          </div>
          <footer className="mt-auto border-t border-border bg-muted/30 text-center py-3 px-4">
            <p className="text-xs text-muted-foreground" data-testid="footer-attribution">
              Developed &amp; maintained by{" "}
              <a
                href="https://bharatcores.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground hover:text-accent transition-colors"
              >
                Bharat Cores Solutions Pvt Ltd
              </a>
            </p>
          </footer>
        </main>
      </div>
      <WelcomeDialog />
    </div>
  );
}

function pageTitle(pathname) {
  if (pathname.startsWith("/super-admin/banks/new")) return "Onboard New Bank";
  if (pathname.startsWith("/super-admin/banks/")) return "Manage Bank";
  if (pathname.startsWith("/super-admin")) return "Super Admin Console";
  if (pathname.startsWith("/applications/new")) return "New Loan Application";
  if (pathname.startsWith("/applications/")) return "Application Detail";
  if (pathname.startsWith("/applications")) return "Loan Applications";
  if (pathname.startsWith("/customers/new")) return "New Customer";
  if (pathname.startsWith("/customers")) return "Customers";
  if (pathname.startsWith("/documents")) return "Document Management";
  if (pathname.startsWith("/approvals")) return "Approvals & Workflow";
  if (pathname.startsWith("/maker-checker")) return "Maker-Checker Queue";
  if (pathname.startsWith("/reports")) return "Reports & MIS";
  if (pathname.startsWith("/mis")) return "MIS Analytics";
  if (pathname.startsWith("/admin/master")) return "Banks, Branches & Configurations";
  if (pathname.startsWith("/admin/policy")) return "Policy Rules";
  if (pathname.startsWith("/admin")) return "Administration";
  if (pathname.startsWith("/settings")) return "Appearance & Settings";
  return "Dashboard";
}
